<template lang="html">
  <UForm
    ref="$form"
    v-slot="{ errors }"
    class="sign-in flex flex-col gap-6"
    :state="model"
    :schema="rules"
    @submit="handleSubmit"
  >
    <div class="sign-in__welcome flex flex-col items-center gap-2 text-center">
      <UIcon :name="brandIcons.logoLargeDark" class="h-14 w-auto" />

      <div class="text-md font-bold">
        Добро пожаловать!
      </div>
      <div class="text-sm">
        Войдите в свой аккаунт, чтобы продолжить покупки
      </div>
    </div>

    <AuthLoginTypeTabs
      v-if="showLoginTypeTabs && !codeStep"
      :model-value="props.type"
      :allowed-types="props.allowedLoginTypes"
      @update:model-value="emit('update:type', $event)"
    />

    <ApiErrorAlert v-if="serverError" :error="serverError" />

    <Transition name="fade" :duration="300" mode="out-in">
      <div v-if="!codeStep" class="sign-in__login">
        <UFormField name="login" required>
          <PhoneInput
            v-if="props.type === LoginInputType.phone"
            v-model="model.login"
            autofocus
            class="w-full"
          />
          <EmailInput
            v-else-if="props.type === LoginInputType.email"
            v-model="model.login"
            autofocus
            class="w-full"
          />
        </UFormField>
      </div>

      <CodeForm
        v-else-if="model.confirmation"
        :model="model"
        @complete="handleSubmit()"
        @resend="tryLogin()"
      />
    </Transition>

    <div class="sign-in__actions">
      <UButton
        v-if="codeStep"
        key="submit"
        type="submit"
        label="Войти"
        class="w-full justify-center"
        :loading="loading"
        :disabled="loading"
      />
      <UButton
        v-else
        key="next"
        label="Продолжить"
        type="submit"
        class="w-full justify-center"
        :loading="loading"
        :disabled="loading"
      />
    </div>

    <div class="sign-in__links flex flex-wrap justify-center gap-2 text-muted">
      <span>У вас нет аккаунта?</span>
      <span
        class="cursor-pointer text-tertiary-500 hover:underline"
        @click="emit('goTo', AuthFormAction.signUp)"
      >Зарегистрироваться</span>
    </div>
  </UForm>
</template>

<script setup lang="ts">
import type { SignInCompletedResponse } from '@repo/api/web/models'
import * as yup from 'yup'

import AuthLoginTypeTabs from './AuthLoginTypeTabs.vue'
import CodeForm from './CodeForm.vue'
import EmailInput from './Input/EmailInput.vue'
import PhoneInput from './Input/PhoneInput.vue'
import { AuthFormAction, LoginInputType } from './type'

import { AuthModel } from '#Auth/models'
import ApiErrorAlert from '#Common/components/ApiErrorAlert.vue'
import { brandIcons } from '~/app/theme/brand'

const props = withDefaults(
  defineProps<{
    type: LoginInputType
    showLoginTypeTabs?: boolean
    allowedLoginTypes?: LoginInputType[]
  }>(),
  {
    showLoginTypeTabs: false,
    allowedLoginTypes: () => [LoginInputType.phone],
  },
)
const emit = defineEmits<{
  submit: []
  goTo: [action: AuthFormAction]
  'update:type': [type: LoginInputType]
}>()

const { service } = useAuth()
const model = ref(AuthModel.createLazy({ longSession: true }))
const codeStep = ref(false)
const loading = ref(false)
const { send, getError, clearError } = useRequest()
const $form = useTemplateRef('$form')

const serverError = computed(() => getError())
const rules = computed(() =>
  yup.object({
    login:
      props.type === LoginInputType.email
        ? yup.string().email().required()
        : yup.string().required(),
    code: model.value.confirmation
      ? yup
          .string()
          .required()
          .min(model.value.confirmation.codeLength || 6)
      : yup.string(),
  }),
)

watchEffect(() => {
  if (serverError.value?.errors) {
    Object.entries(serverError.value.errors).forEach(([key, value]) => {
      $form.value?.setErrors([{ message: value[0]!, name: key }], key)
    })
  }
})

function resetLoginState() {
  model.value.login = ''
  model.value.code = undefined
  model.value.confirmation = undefined
  codeStep.value = false
  clearError()
}

watch(() => props.type, (type, prevType) => {
  if (prevType === undefined || type === prevType) {
    return
  }

  resetLoginState()
})

async function handleCompleated(completed: SignInCompletedResponse) {
  await service.signIn(completed.token!)
  emit('submit')
}
async function tryLogin() {
  try {
    loading.value = true
    const response = await send(() => service.login(model.value), {
      throwError: true,
      collectApiErrors: true,
    })

    /**
     * В ряде случаев сервер сразу возвращает токен и нам не нужно проходить этап ввода кода.
     */
    if (response.completed) {
      await handleCompleated(response.completed)
      clearError()
      return
    }

    model.value.confirmation = response.confirmation!
    codeStep.value = true
    clearError()
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}
async function handleCodeEnter() {
  try {
    loading.value = true

    const response = await send(
      () =>
        service.confirm({
          challengeId: model.value.confirmation!.challengeId!,
          code: model.value.code!,
        } as any),
      {
        throwError: true,
        collectApiErrors: true,
      },
    )

    await handleCompleated(response)

    clearError()
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}
function handleSubmit() {
  if (loading.value) return

  if (!codeStep.value) {
    return tryLogin()
  }

  return handleCodeEnter()
}
</script>
